import { useState } from 'react';

import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { KioskScreen } from '@/components/KioskScreen';
import { useTranslation } from '@/i18n/useTranslation';
import type { Screen } from '@/lib/kioskSession';
import { useKioskSession } from '@/lib/kioskSession';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

export interface DifferentEmailDialogProps {
  readonly emailOnFile: string | undefined;
  /**
   * Where both answers lead: the screen a RETURNING match continues to. `RoutingScreen` passes its own
   * answer, so the two paths into the returning flow cannot drift apart.
   */
  readonly continueTo: Screen;
}

/**
 * The different-email prompt [decisions 0030, 0042; Figma `Email / 02 · Email dialog`, 12021:41256 · 41254 ·
 * 41252].
 *
 * Reached only by the one secondary match path: the enumerated set {fuzzy last name · last-4 SSN · day/month
 * DOB} matched a single record whose email differs from the one typed. Email is the only droppable factor,
 * and this prompt is the only reason dropping it is allowed at all.
 *
 * **It is a choice, and both answers continue into the returning flow** [KID-2 — *"offers 'use the new one'
 * or 'use my account email instead' — and only then continues into the returning flow"*]. Identity is
 * already settled by the other three factors, so neither answer ends the visit. ⛔ Until ISS-145 the
 * confirming button called `endCandidateSession`: a candidate the kiosk had just found was sent back to
 * Welcome with nothing written and nothing said.
 *
 * - **Continue** keeps the email in the field — the one they typed, or a typo they fixed in place.
 * - **Use my account email instead** keeps the address on file.
 *
 * Either way the chosen address becomes the session's email, and the screen a returning match continues to
 * opens with it [the prototype's `resolveEmailDialog` → `profile-update`, which governs the flow — 0155].
 * A mistyped email is corrected in the field itself, which is how the frame offers it: it draws these two
 * buttons and no third one at every width, and so do the prototype, 0030 and KID-2. **This screen
 * writes nothing.** The write is that screen's, against the matched `candidateId` — an update, never a
 * second record — so the new email neither overwrites silently nor duplicates [0030].
 *
 * Rendered as a screen rather than an overlay: it must survive an outside click — a candidate resting a palm
 * on a touchscreen dismisses a modal without meaning to — and a dialog nobody can accidentally dismiss is a
 * screen.
 */
export function DifferentEmailDialog({ emailOnFile, continueTo }: DifferentEmailDialogProps) {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const typedEmail = useKioskSession((state) => state.factors.email);
  const setFactor = useKioskSession((state) => state.setFactor);
  const goTo = useKioskSession((state) => state.goTo);

  // Pre-filled with what they typed, so Continue with no edit keeps the new email [prototype `openEmailDialog`].
  const [email, setEmail] = useState(typedEmail.trim());

  const continueWith = (chosen: string): void => {
    setFactor('email', chosen);
    goTo(continueTo);
  };

  return (
    <KioskScreen>
      {/*
        The frame's `Dialog`: white, 12px radius, Elevation 3, 24px padding and a 20px rhythm at iPad and
        desktop. At 375 it keeps the 24px top and bottom and draws NO side padding (`p24/0/24/0`), inside
        16px margins — built as drawn.
        The width is the shell's card width (553, 768 at 1440), not the frame's 580/800: every screen in this
        app still sits on the pre-relayout shell, and a dialog wider than its own top bar would be worse than
        either. Recorded in the evidence notes.
      */}
      <section
        aria-labelledby="different-email-heading"
        className="mx-4 flex w-[calc(100%-2rem)] max-w-[553px] flex-col gap-5 rounded-[var(--radius-md)] bg-[var(--card)] py-6 shadow-[shadow:var(--elevation-3)] sm:mx-0 sm:w-full sm:px-6 min-[1440px]:max-w-[768px]"
      >
        <h1
          className="kiosk-heading-focus w-full text-base font-semibold leading-6 text-[var(--foreground)]"
          id="different-email-heading"
          ref={heading}
          tabIndex={-1}
        >
          {t('differentEmail.heading')}
        </h1>
        {/*
          The sentence NAMES the address on file, so it is only rendered when there is one. A record with no
          email on file still matched on the other three factors; it gets the field and Continue, and no
          "use my account email" — there is nothing on file to use.
        */}
        {emailOnFile !== undefined && (
          <p className="w-full text-sm leading-5 text-[var(--muted-foreground)] [overflow-wrap:anywhere]">
            {t('differentEmail.body', { email: emailOnFile })}
          </p>
        )}
        <Field
          id="different-email-email"
          inputMode="email"
          label={t('differentEmail.emailLabel')}
          onChange={setEmail}
          type="email"
          value={email}
        />
        <div className="flex w-full flex-col gap-2">
          <Button
            fullWidth
            // An emptied field is not a choice of "no email": it keeps what they typed at check-in, as the
            // prototype does (`if(email) capturedIdentity.email = email`).
            onClick={() => {
              continueWith(email.trim() === '' ? typedEmail.trim() : email.trim());
            }}
          >
            {t('differentEmail.continue')}
          </Button>
          {emailOnFile !== undefined && (
            <Button
              fullWidth
              onClick={() => {
                continueWith(emailOnFile);
              }}
              variant="secondary"
            >
              {t('differentEmail.useOnFile')}
            </Button>
          )}
        </div>
      </section>
    </KioskScreen>
  );
}
